import { tr } from './i18n';
import { ago, duration } from './ui';
import type { Tone } from './status';

/** An alert as the API returns it (title/message are English server text, kept for "Details"). */
export interface AlertRow {
  id: string;
  type: string;
  severity: 'info' | 'warning' | 'critical';
  state?: string;
  title: string;
  message: string;
  details?: Record<string, any> | null;
  subject_id?: string | null;
  subject_name?: string | null;
  last_at: string;
  occurrences: number;
}

export interface AlertCopy {
  tone: Tone;
  /** "Urgent" / "Needs attention" / "For your information". */
  level: string;
  title: string;
  /** What to do about it, in plain words. */
  todo: string;
  /** Link to where it can be fixed. */
  link?: { to: string; label: string };
}

/** The plain-language version of an alert, in the current language. */
export function alertCopy(a: AlertRow, base: string): AlertCopy {
  const name = a.subject_name ?? tr('a computer');
  const d = a.details ?? {};
  const tone: Tone = a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : 'info';
  const level = a.severity === 'critical' ? tr('Urgent') : a.severity === 'warning' ? tr('Needs attention') : tr('For your information');
  const computer = a.subject_id ? { to: `${base}/devices/${a.subject_id}`, label: tr('Open the computer') } : undefined;
  switch (a.type) {
    case 'backup_failed':
      return { tone, level, title: tr("A backup didn't finish on {name}", { name }), todo: tr('This often fixes itself at the next backup. If it keeps happening, open the computer to see why.'), link: computer };
    case 'backup_missed':
      return {
        tone,
        level,
        title: d.lastSuccessAt ? tr('{name} has not been backed up for {time}', { name, time: duration(Date.now() - Date.parse(d.lastSuccessAt)) }) : tr('{name} has not been backed up yet', { name }),
        todo: tr('Turn the computer on and connect it to the internet. It backs up automatically.'),
        link: computer,
      };
    case 'device_offline':
      return {
        tone,
        level,
        title: d.lastSeenAt ? tr('{name} has not connected for {time}', { name, time: duration(Date.now() - Date.parse(d.lastSeenAt)) }) : tr('{name} has not connected for a long time', { name }),
        todo: tr('If it was replaced or is no longer used, remove it. If it was lost or stolen, block it. Its backups are kept either way.'),
        link: computer,
      };
    case 'quota_threshold':
      return {
        tone,
        level,
        title: tr('Your storage is {pct}% full', { pct: d.percent ?? '' }),
        todo: tr('Backups continue. To make room, upgrade your subscription or keep less history in your backup plans.'),
        link: { to: `${base}/billing`, label: tr('See usage') },
      };
    case 'suspicious_change':
      return {
        tone,
        level,
        title: tr('Unusual changes on {name}', { name }),
        todo: tr('Many files were changed or deleted at once. This can be a sign of ransomware. Older backups are kept safe for 14 days while you check. If you are not sure, contact your IT person.'),
        link: computer,
      };
    case 'verification_failed':
      return {
        tone,
        level,
        title: d.drill ? tr('The automatic test restore failed for {name}', { name }) : tr('An automatic check of the backups failed for {name}', { name }),
        todo: tr('Please contact support so we can look into it.'),
        link: computer,
      };
    default:
      return { tone, level, title: a.title, todo: a.message, link: computer };
  }
}

export const lastHappened = (a: AlertRow) =>
  a.occurrences > 1 ? tr('{time}, {n} times', { time: ago(a.last_at), n: a.occurrences }) : ago(a.last_at);
